import translate from '@translate';
import moment from 'moment';
import { ConstantsCodesObjectType } from 'src/types/constantsCodes';
import getRecursiveValue from 'src/utils/getRecursiveValue';
import { formatNumber, isNumericValue } from 'src/utils/formatNumber';
import {
    ChangelogDataValueType,
    HistoryFormatedDataType,
    HistoryUpdatesItemsTranslateKeyType,
    ChangeValueType,
    ChangelogValuesType,
    KeyCallbackType,
    TransalteRowType,
} from './types';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import { ReactNode } from 'react';
import { CustomIconFceType } from '.';

/** History diffs may contain boolean `false` (e.g. EAN active). `isDefined(false)` is false. */
export function isPresentHistoryValue<T>(value: T): value is NonNullable<T> {
    return value !== undefined && value !== null;
}

export const formatDate = (date: ChangeValueType) => {
    if (typeof date === 'boolean' || !date || date === '-') {
        return '-';
    }
    return moment(date).format('LLL');
};

export const formatOnlyDate = (date: ChangeValueType) => {
    if (typeof date === 'boolean' || !date || date === '-') {
        return '-';
    }
    return moment(date).format('LL');
};


export const formatNumberValue = (value: ChangeValueType): ChangeValueType => {
    if (value === null || value === undefined || value === '' || value === '-' || typeof value === 'boolean') {
        return value;
    }
    if (isNumericValue(value)) {
        return formatNumber(value);
    }
    return value;
};

export const formatValue = (value: ChangeValueType, format?: string) => {
    if (format === 'date') {
        return formatDate(value);
    }
    if (format === 'onlydate') {
        return formatOnlyDate(value);
    }
    if (format === 'number') {
        return formatNumberValue(value);
    }
    return value;
};

/** Empty for CREATE history filtering: null, undefined, '' and '-' (e.g. empty dates). 0 / false stay visible. */
export const isEmptyHistoryValue = (value: ChangeValueType | undefined): boolean => {
    return value === null || value === undefined || value === '' || value === '-';
};

/** True when at least one history row has content to display (hides empty timeline headers). */
export const hasVisibleHistoryRows = (rows: TransalteRowType[] | undefined): boolean => {
    return (rows ?? []).some((row) => !!row);
};

export interface TranslateKeyOptions {
    hideEmpty?: boolean;
}

/** Changelog diff `{ new, old }`. `null` is a real value, not a missing key. */
const isChangeRecord = (
    value: unknown
): value is { new?: ChangeValueType; old?: ChangeValueType } =>
    typeof value === 'object' && value !== null && ('new' in value || 'old' in value);

// Helper to check if a nested key exists in an object (even if value is null)
const hasNestedKey = (obj: Record<string, unknown> | null | undefined, keyPath: string): boolean => {
    const keys = keyPath.split('.');
    let current: unknown = obj;
    for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (current === null || current === undefined || typeof current !== 'object') {
            return false;
        }
        if (!(k in current)) {
            return false;
        }
        // Only traverse deeper if not at the last key
        if (i < keys.length - 1) {
            current = (current as Record<string, unknown>)[k];
        }
    }
    return true;
};

export const transalteKey = (
    data: ChangelogValuesType,
    key: string,
    translateKey: HistoryUpdatesItemsTranslateKeyType,
    format?: string,
    options?: TranslateKeyOptions
): string | null => {
    if (!data) {
        return null;
    }
    const dataVal = getRecursiveValue(key, data);
    // Check if the key exists in data (even if value is null)
    const keyExists = hasNestedKey(data, key);
    
    // Return null only if key doesn't exist AND value is falsy (but not 0)
    // If key exists, we want to show it even if value is null (e.g., for date fields showing '-')
    if (!keyExists && !dataVal && dataVal !== 0) {
        return null;
    }

    let keyName = '';
    if (typeof translateKey === 'object') {
        keyName = `${translateKey
            // History passes a literal '-' separator part that is not a translation key.
            .map((key: string): string => (key.trim() === '-' ? key : translate(key)))
            .join(' ')}: `;
    } else {
        keyName = `${translate(translateKey)}: `;
    }

    if (isChangeRecord(dataVal)) {
        const hasOld = Object.prototype.hasOwnProperty.call(dataVal, 'old');
        const hasNew = Object.prototype.hasOwnProperty.call(dataVal, 'new');
        const formattedOld = hasOld ? formatValue(dataVal.old ?? null, format) : undefined;
        const formattedNew = hasNew ? formatValue(dataVal.new ?? null, format) : undefined;
        if (
            options?.hideEmpty &&
            isEmptyHistoryValue(formattedOld as ChangeValueType | undefined) &&
            isEmptyHistoryValue(formattedNew as ChangeValueType | undefined)
        ) {
            return null;
        }
        if (hasOld && hasNew) {
            return `${keyName}${formattedOld} -> ${formattedNew}`;
        }
        return `${keyName}${hasNew ? formattedNew : formattedOld}`;
    } else {
        const formatted = formatValue(dataVal, format);
        if (options?.hideEmpty && isEmptyHistoryValue(formatted as ChangeValueType | undefined)) {
            return null;
        }
        return `${keyName}${formatted}`;
    }
};

export const formatDataCodes = (
    data: ChangelogDataValueType,
    codesObject: ConstantsCodesObjectType
): ChangelogDataValueType => {
    if (!isPresentHistoryValue(data) || !codesObject) {
        return data;
    }

    if (typeof data === 'object') {
        if ('new' in data || 'old' in data) {
            const newData: ChangelogDataValueType = { new: null, old: null };
            if (isPresentHistoryValue(data.new)) {
                newData.new = codesObject[String(data.new)]?.label;
            } else {
                newData.new = null;
            }
            if (isPresentHistoryValue(data.old)) {
                newData.old = codesObject[String(data.old)]?.label;
            } else {
                newData.old = null;
            }
            return newData;
        }
    } else {
        return codesObject[String(data)]?.label;
    }

    return data;
};

export const formatDataCallback = (
    data: ChangelogDataValueType,
    callback: KeyCallbackType
): ChangelogDataValueType => {
    if (!isPresentHistoryValue(data) || !callback) {
        return data;
    }

    if (typeof data === 'object') {
        if ('new' in data || 'old' in data) {
            const newData: ChangelogDataValueType = { new: null, old: null };
            if (isPresentHistoryValue(data.new)) {
                newData.new = callback(data.new as ChangeValueType);
            } else {
                newData.new = null;
            }
            if (isPresentHistoryValue(data.old)) {
                newData.old = callback(data.old as ChangeValueType);
            } else {
                newData.old = null;
            }
            return newData;
        }
    } else {
        return callback(data);
    }

    return data;
};

export const getIcon = (row: HistoryFormatedDataType, customIconFce?: CustomIconFceType): ReactNode => {
    if (row.type === 'create') return <AddIcon fontSize="small" />;
    if (customIconFce) {
        const customIcon = customIconFce(row);
        if (customIcon) {
            return customIcon;
        }
    }

    if (typeof row.data?.STATUS === 'object') {
        return (
            <LowPriorityIcon
                fontSize="small"
                style={{
                    transform: 'rotateX(180deg)',
                }}
            />
        );
    }

    return <EditIcon fontSize="small" />;
};
